import React, {useEffect, useState} from 'react';
import {Table} from 'antd';
import {ModelDTO} from 'hemwb-api';
import {useGetOverviewColumns} from './useGetOverviewColumns';
import {useNavigate} from 'react-router-dom';
import {getUrlRoute, MRAppRoutes} from '../../../router/routes';
import {DownOutlined, RightOutlined} from '@ant-design/icons';
import styles from './ModelFamilyDisplay.module.scss';

interface ModelFamilyDisplayProps {
  models: (ModelDTO & {children?: ModelDTO[]})[] | undefined;
  currentModelUuid: string;
  loading?: boolean;
  isExternalUser: boolean;
}

/**
 * Display component for showing a model family tree in ModelDetail
 * - No sorting
 * - Expand/collapse functionality for rows with children
 * - Currently viewed model is highlighted and non-clickable
 * - Other rows navigate on click
 */
export const ModelFamilyDisplay: React.FC<ModelFamilyDisplayProps> = ({
  models,
  currentModelUuid,
  loading = false,
  isExternalUser,
}) => {
  const navigate = useNavigate();
  const columns = useGetOverviewColumns(isExternalUser);

  // Track expanded rows (expand all by default)
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Initialize expanded keys when models load (expand all by default)
  useEffect(() => {
    if (models && !initialized) {
      const keys: string[] = [];
      const collectKeys = (items: (ModelDTO & {children?: ModelDTO[]})[]) => {
        items.forEach((item) => {
          if (item.children && item.children.length > 0) {
            keys.push(item.uuid);
            collectKeys(item.children);
          }
        });
      };
      collectKeys(models);
      setExpandedRowKeys(keys);
      setInitialized(true);
    }
  }, [models, initialized]);

  // Handle Ant Design's expand/collapse
  const handleExpandButtonClick = (uuid: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRowKeys((prev) => (prev.includes(uuid) ? prev.filter((key) => key !== uuid) : [...prev, uuid]));
  };

  // Handle row click - only navigate if NOT the current model
  const handleRowClick = (record: ModelDTO) => ({
    onClick: () => {
      if (record.uuid !== currentModelUuid) {
        navigate(getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid: record.uuid}));
      }
    },
  });

  return (
    <div className={styles.tableWrapper}>
      <Table
        dataSource={models ?? []}
        columns={columns}
        loading={loading}
        pagination={false}
        rowKey={(record) => record.uuid}
        className={styles.familyDisplay}
        rowClassName={(record: ModelDTO & {isChild?: boolean}) => {
          // Highlight and disable interaction for current model
          if (record.uuid === currentModelUuid) {
            return styles.currentRow;
          }
          // Handle child rows
          if (record.isChild) {
            return styles.childRow;
          }
          return '';
        }}
        expandable={{
          expandedRowKeys,
          expandIconColumnIndex: 1,
          expandRowByClick: false,
          expandIcon: ({record: model, expanded}) => {
            const hasChildren = !!(model as any)?.children?.length;
            const level = (model as any)?.level || 0;

            // Show expand icon for any row that has children (parent or child)
            if (!hasChildren) {
              return null;
            }

            // Calculate left margin for nested expand icons
            // Level 0: 0px, Level 1: 48px, Level 2: 96px, etc.
            const leftMargin = level * 48;

            return (
              <button
                className={`ant-table-row-expand-icon customExpandIcon ${
                  expanded ? 'ant-table-row-expand-icon-expanded' : 'ant-table-row-expand-icon-collapsed'
                }`}
                onClick={(e) => {
                  handleExpandButtonClick(model.uuid, e);
                }}
                aria-label={expanded ? 'Collapse row' : 'Expand row'}
                style={{marginLeft: `${leftMargin}px`, marginRight: 16}}
              >
                {expanded ? (
                  <DownOutlined style={{fontSize: 16, color: '#006A63'}} />
                ) : (
                  <RightOutlined style={{fontSize: 16, color: '#000000'}} />
                )}
              </button>
            );
          },
        }}
        onRow={handleRowClick}
      />
    </div>
  );
};
